// Order detail — shows order + saga status (polls if in progress).
// Operate mode: 2-column layout, primary info left, sidebar right.

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useParams, useSearchParams, Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, RefreshCw, XCircle, CheckCircle2, Clock, Pencil, X, Printer, Download, MapPin, Eye, PackageX, Send, Minus, Plus } from "lucide-react"
import { api, downloadFile, get, post } from "@/lib/api"
import { cn } from "@/lib/utils"
import { StatusPill } from "@/components/ui/status"
import { IndexBadge } from "@/components/ui/data-table"
import { Button } from "@/components/ui/button"
import { MobileActionBar } from "@/components/ui/mobile-action-bar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { MoneyInput } from "@/components/ui/money-input"
import { Segmented } from "@/components/ui/segmented"
import { toast } from "sonner"
import { confirm } from "@/components/ui/confirm"
import { money, phoneDisplay, formatBestStatus, shortOrderId } from "@/lib/format"
import { fetchProvincesApi, matchAdminDivisions } from "@/lib/provincesApi"
import { AddressInput } from "@/components/address/AddressInput"
import type { AreaIssue } from "@shoptrack/common/admin-areas"
import { AddressMapPickerModal, type MapPickedAddress } from "@/components/AddressMapPickerModal"
import { resolveAdministrativeAddress } from "@/lib/vietnamAddressResolver"
import { OrderMoneyDetails, type OrderMoneyView } from "@/components/orders/order-money"
import { ShipPaymentPanel } from "@/components/orders/ShipPaymentPanel"
import { LiveJourney, journeySteps, type LiveTrackingResult } from "@/components/orders/LiveJourney"
import { ReturnClaimDialog, ReturnClaimSent, claimExpired, formatDeadline } from "@/components/orders/ReturnClaimDialog"
import { useNppName } from "@/components/layout/shell-model"
import { getAuthState } from "@/lib/auth"
import { useUnitCan } from "@/lib/unitAccess"
import { isCancelledStatus } from "@/lib/orderStatus"
import { printWaybills } from "@/lib/printWaybills"
import { fromLocalInput, sendAtError, sendAtLabel, toLocalInput, SEND_AT_MIN_LEAD_MS } from "@/lib/sendAt"
import { PhoneInput } from "@/components/ui/phone-input"
import { ProductPickerDialog, type ProductOpt } from "@/components/orders/ProductPickerDialog"
import { isValidVnPhone } from "@/lib/format"

interface OrderItem {
  id: string
  productId?: string | null
  productName: string
  sku?: string | null
  price: number
  cost: number
  qty: number
  weightGram: number
  totalAmount: number
}

interface Order {
  /** Shop trả ship qua QR — có phiếu thì hiện QR / kết quả duyệt */
  shipPrepay?: { status: string } | null
  id: string
  clientOrderId: string
  channel: string
  cod: number
  cost: number
  status: string
  shipmentCode: string | null
  address: string
  lat?: number | null
  lng?: number | null
  geoAddress?: string | null
  sagaId: string | null
  createdAt: string
  customerId?: string
  customer: { name: string; phone: string | null }
  items?: OrderItem[]
  shipment: {
    code: string | null
    productName: string
    weightGram: number
    lengthCm?: number | null
    widthCm?: number | null
    heightCm?: number | null
    destCity: string
    destDistrict?: string | null
    destWard: string
    statusCode: string | null
    statusText: string | null
    declaredValue?: number
    carrierUpdatedAt?: string | null
    lastSyncedAt: string | null
  } | null
  /** Tiền của người xem trên đơn (GĐ7); null = đơn cũ chưa có sổ tiền. */
  money?: OrderMoneyView | null
  /** Shop sở hữu đơn (khác shop đang đăng nhập khi Đại lý / CTV xem đơn cấp dưới). */
  shopId?: string
  /** Đơn của cấp dưới: chỉ xem — không sửa, hủy, in, thử lại. */
  readOnly?: boolean
  /** Nhân viên của đơn vị đã lên đơn này (docs/KE_HOACH_THANH_VIEN_DON_VI.md); null = chủ lên / đơn cũ */
  createdByStaff?: { userId: string; name: string | null } | null
  /** Đơn hẹn gửi (docs/KE_HOACH_HEN_GIO_GUI_DON.md): giờ hẹn; đơn của mình có thêm kiện / nơi nhận đang chờ gửi */
  schedule?: {
    sendAt: string
    status?: string
    lastError?: string | null
    shipment?: ScheduleShipment
    shopPaysShipping?: boolean
  } | null
}

/** Kiện / nơi nhận của đơn hẹn gửi (chưa có vận đơn) */
interface ScheduleShipment {
  productName: string
  weightGram: number
  destCity: string
  destDistrict?: string | null
  destWard: string
  length?: number | null
  width?: number | null
  height?: number | null
}

interface Saga {
  id: string
  type: string
  state: string
  step: number
  attempts: number
  lastError: string | null
  createdAt: string
  finishedAt: string | null
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  // Đại lý / CTV mở đơn của shop trong nhánh: /orders/:id?shopId=<shop> → chế độ chỉ xem.
  const [searchParams] = useSearchParams()
  const ownerShopId = searchParams.get("shopId")
  const [editOpen, setEditOpen] = useState(false)
  const [trackingOpen, setTrackingOpen] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [claimOpen, setClaimOpen] = useState(false)
  const [claimSent, setClaimSent] = useState<{ deadline: string | null } | null>(null)
  const nppName = useNppName()
  // Nhân viên: thao tác theo quyền chủ cấp — chủ đơn vị: mọi nút như trước
  const { can, seesMoney } = useUnitCan()

  const qc = useQueryClient()
  const order = useQuery({
    queryKey: ["order", id, ownerShopId],
    queryFn: () => get<{ order: Order }>(`/orders/${encodeURIComponent(id ?? "")}${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`),
    refetchInterval: (query) => {
      const data = query.state.data?.order
      const isFinal =
        data?.status === "giao-ok" ||
        data?.status === "hoan" ||
        data?.status === "failed_shipment" ||
        // Đang hủy (chờ BEST) vẫn hỏi nhanh để thấy "Đã hủy" ngay khi xong
        (isClosedCancelled(data?.status) && data?.status !== "cancelling")
      return isFinal ? 30_000 : 10_000
    },
    refetchOnWindowFocus: true,
  })

  const inProgressStates = ["pending", "running", "compensating"]
  const readOnly = Boolean(order.data?.order?.readOnly)
  // Tiến trình xử lý (saga) chỉ chủ đơn xem được — đơn cấp dưới thì bỏ qua.
  const sagaId = readOnly ? null : order.data?.order?.sagaId

  const saga = useQuery({
    queryKey: ["saga", sagaId],
    queryFn: () => get<{ saga: Saga }>(`/orders/sagas/${sagaId}`),
    enabled: Boolean(sagaId),
    refetchInterval: (query) => {
      const state = query.state.data?.saga?.state
      return state && inProgressStates.includes(state) ? 2_000 : false
    },
  })

  useEffect(() => {
    if (saga.data?.saga?.state === "done") {
      order.refetch()
    }
  }, [saga.data?.saga?.state])

  const retrySaga = useMutation({
    mutationFn: () => post<{ ok: boolean }>(`/orders/sagas/${sagaId}/retry`),
    onSuccess: () => {
      toast.success("Đã gửi yêu cầu thử lại")
      saga.refetch()
      order.refetch()
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const sendNow = useMutation({
    mutationFn: () => post<{ outcome: "shipment" | "prepay" | "cancelled"; amount?: number }>(`/orders/${encodeURIComponent(id ?? "")}/send-now`),
    onSuccess: (r) => {
      if (r.outcome === "prepay") toast.success(`Chuyển khoản ${money(r.amount ?? 0)} tiền ship theo mã QR`, { duration: 8_000 })
      else if (r.outcome === "cancelled") toast.info("Đơn không còn hẹn gửi")
      else toast.success("Đã gửi sang hãng")
      order.refetch()
      void qc.invalidateQueries({ queryKey: ["orders"] })
    },
    onError: (e) => {
      toast.error((e as Error).message)
      order.refetch()
    },
  })

  async function onSendNow() {
    const ok = await confirm({ title: "Gửi đơn này ngay?", confirmLabel: "Gửi ngay", cancelLabel: "Đóng" })
    if (ok) sendNow.mutate()
  }

  // In: mở hộp in (điện thoại: PDF ở thẻ mới); Tải: lưu PDF về máy — cùng file máy chủ ghép (lib/printWaybills)
  async function handleWaybill(code: string, kind: "print" | "download") {
    setPrinting(true)
    try {
      if (kind === "print") await printWaybills([code])
      else await downloadFile("/shipping/waybills/download", `van-don_${code}.pdf`, { codes: [code] })
      order.refetch()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPrinting(false)
    }
  }

  async function onCancel() {
    const ok = await confirm({
      title: "Hủy đơn hàng này?",
      description: "Nếu đơn đã có vận đơn, chúng tôi sẽ tự động gửi yêu cầu hủy với hãng vận chuyển.",
      tone: "danger",
      confirmLabel: "Hủy đơn",
      cancelLabel: "Đóng",
    })
    if (!ok) return
    try {
      // 200 {status:'huy'} = hủy xong · 202 {status:'cancelling'} = đang chờ BEST xác nhận ·
      // 200 {message} = đơn đã ở trạng thái cuối (hủy / hoàn) từ trước
      const r = await post<{ orderId: string; status?: string; sagaId?: string | null; message?: string }>(`/orders/${id}/cancel`)
      if (r?.message) toast.info(r.message)
      else if (r?.status === "cancelling") toast.success("Đã gửi yêu cầu hủy — đang chờ hãng xác nhận")
      else toast.success("Đã hủy đơn")
      order.refetch()
      // Hủy xong máy chủ đã trả tồn kho — không đợi hết staleTime
      for (const key of ["products", "products-active", "product", "orders", "overview", "reports.revenue"]) {
        void qc.invalidateQueries({ queryKey: [key] })
      }
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  if (order.isLoading) return <div className="text-muted">Đang tải…</div>
  if (!order.data) return <div className="text-danger">Không tìm thấy đơn</div>

  const o = order.data.order
  const effectiveShipmentCode = o.shipment?.code || o.shipmentCode || null
  const canClaimReturn = !readOnly && can("orders.edit") && o.money?.status === "returned" && o.money?.returnStage === "returned"
  const claimDeadline = claimSent?.deadline ?? o.money?.returnClaimDeadline ?? null
  const claimClosed = claimExpired(claimDeadline)
  const canEdit = !readOnly && can("orders.edit") && o.status !== "giao-ok" && o.status !== "hoan" && !isClosedCancelled(o.status)
  const printCode = readOnly || !can("orders.print") ? null : effectiveShipmentCode
  // Phiếu tiền ship QR: đơn của mình — quyền trả ship QR hoặc xem tiền; đơn cấp dưới — xem cấp dưới + xem tiền
  const showShipPayment = Boolean(o.shipPrepay) && (readOnly ? can({ all: ["network.view", "money.view"] }) : can({ any: ["orders.ship_prepay", "money.view"] }))
  const itemCount = o.items?.reduce((s, x) => s + x.qty, 0) ?? 0
  const itemTotal = o.items?.reduce((s, x) => s + (x.totalAmount || x.price * x.qty), 0) ?? 0
  const sched = o.status === "hen-gio" ? o.schedule ?? null : null
  const parcel = o.shipment ?? sched?.shipment ?? null
  // Gửi ngay: đơn Shop trả ship tới giờ là phải chuyển tiền ship — cần cả quyền đó (máy chủ cũng chặn)
  const canSendNow = canEdit && Boolean(sched) && (!sched?.shopPaysShipping || can("orders.ship_prepay"))
  const fullAddress = [o.address, parcel?.destWard, parcel?.destDistrict, parcel?.destCity].filter(Boolean).join(", ") || o.address
  // Trạng thái bưu cục chỉ khi đã có mã vận đơn VÀ BEST đã báo (có mã trạng thái / giờ cập nhật). Chữ mặc
  // định lúc vừa tạo vận đơn ("Đang giao hàng") không phải trạng thái thật.
  const carrierReported = Boolean(effectiveShipmentCode && (o.shipment?.statusCode || o.shipment?.carrierUpdatedAt))
  const carrierUpdatedAt = o.shipment?.carrierUpdatedAt || o.shipment?.lastSyncedAt

  return (
    <div className="mx-auto max-w-6xl">
      {/* Đầu trang: mã đơn rút gọn + trạng thái + thao tác (máy tính). Mã đầy đủ / tham chiếu / giờ tạo / kênh ở dòng phụ */}
      <div className="mb-3 flex items-start gap-3">
        <Link
          to={readOnly ? networkOrdersPath() : "/orders"}
          aria-label="Quay lại"
          title="Quay lại"
          className="mt-0.5 inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-rule bg-raise text-inkSoft hover:text-accent desktop:size-9"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 title={o.id}>{shortOrderId(o.id)}</h1>
            <StatusPill status={o.status} />
            {sched && <span className="font-mono text-sm tabular-nums text-inkSoft">{sendAtLabel(sched.sendAt)}</span>}
            {readOnly && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-paperSoft px-2 py-0.5 text-xs font-medium text-inkSoft">
                <Eye className="size-3.5" aria-hidden /> Chỉ xem
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted">
            <span className="select-all font-mono">{o.id}</span>
            {o.clientOrderId && o.clientOrderId !== o.id && (
              <>
                {" · "}Tham chiếu <span className="font-mono">{o.clientOrderId}</span>
              </>
            )}
            {" · "}
            {new Date(o.createdAt).toLocaleString("vi-VN")} · {o.channel}
            {o.createdByStaff && (
              <>
                {" · "}Người lên đơn <span className="font-medium text-inkSoft">{o.createdByStaff.name ?? "nhân viên"}</span>
              </>
            )}
          </p>
        </div>
        {(canEdit || printCode) && (
          <div className="hidden shrink-0 items-center gap-2 desktop:flex">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={onCancel} className="text-danger hover:bg-danger/5 hover:text-danger">
                <XCircle /> Hủy đơn
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil /> Sửa đơn
              </Button>
            )}
            {canSendNow && (
              <Button size="sm" disabled={sendNow.isPending} onClick={onSendNow}>
                <Send /> Gửi ngay
              </Button>
            )}
            {printCode && (
              <Button size="sm" variant="outline" disabled={printing} onClick={() => handleWaybill(printCode, "download")}>
                <Download /> Tải PDF
              </Button>
            )}
            {printCode && (
              <Button size="sm" disabled={printing} onClick={() => handleWaybill(printCode, "print")}>
                <Printer /> In vận đơn
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Shop trả ship: QR chuyển khoản / kết quả duyệt (đơn đi luồng thường không có phiếu → không hiện) */}
      {showShipPayment && (
        <div className="mb-3">
          <ShipPaymentPanel orderId={o.id} ownerShopId={ownerShopId} orderStatus={o.status} />
        </div>
      )}

      {/* Máy tính: trái = người nhận / sản phẩm / vận đơn, phải = tiền / tiến trình / lịch sử. Điện thoại: một cột theo thứ tự đó */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-3">
          <Section
            title="Người nhận"
            aside={
              // Khách thuộc danh bạ của chính đơn vị — đơn của cấp dưới (chỉ xem) không mở được
              !readOnly && o.customerId ? (
                <Link to={`/customers?id=${encodeURIComponent(o.customerId)}`} className="text-xs font-medium text-accent hover:underline">
                  Xem khách
                </Link>
              ) : null
            }
          >
            <p className="text-sm">
              <span className="font-medium text-ink">{o.customer.name}</span>
              {o.customer.phone && (
                <>
                  <span className="text-muted"> · </span>
                  <a href={`tel:${o.customer.phone}`} className="tabular-nums text-ink hover:text-accent">
                    {phoneDisplay(o.customer.phone)}
                  </a>
                </>
              )}
            </p>
            <p className="mt-0.5 text-sm text-inkSoft">{fullAddress}</p>
            {o.lat != null && o.lng != null && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${o.lat},${o.lng}`}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs text-accent hover:underline"
              >
                <MapPin className="size-3" /> Định vị GPS ({o.lat.toFixed(4)}, {o.lng.toFixed(4)})
              </a>
            )}
          </Section>

          {o.items && o.items.length > 0 && (
            <Section title={`Sản phẩm (${itemCount})`} aside={<span className="font-mono text-sm font-semibold text-accent">{money(itemTotal)}</span>}>
              <ul className="-my-1.5 divide-y divide-rule/60">
                {o.items.map((it, i) => (
                  <li key={it.id} className="flex items-center gap-3 py-1.5">
                    <IndexBadge n={i + 1} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">{it.productName}</p>
                      <p className="text-xs text-muted">
                        {it.sku && <span className="font-mono">{it.sku} · </span>}
                        {it.weightGram > 0 && <>{it.weightGram}g/sp · </>}
                        <span className="tabular-nums text-inkSoft">
                          {it.qty} × {money(it.price)}
                        </span>
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-sm font-medium text-ink">{money(it.totalAmount || it.price * it.qty)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {sched && (
            <Section title="Hẹn gửi">
              {sched.lastError && <p className="mb-2.5 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{sched.lastError}</p>}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <Fact label="Giờ hẹn">
                  <span className="font-mono tabular-nums">{sendAtLabel(sched.sendAt)}</span>
                </Fact>
                {sched.shipment && (
                  <>
                    <Fact label="Cân nặng">
                      <span className="font-mono">{sched.shipment.weightGram}g</span>
                    </Fact>
                    <Fact label="Tên hàng" className="col-span-2 sm:col-span-3">
                      {sched.shipment.productName}
                    </Fact>
                  </>
                )}
              </dl>
            </Section>
          )}

          {(effectiveShipmentCode || o.shipment) && (
            <Section
              title="Vận đơn"
              aside={
                effectiveShipmentCode ? (
                  <button
                    type="button"
                    onClick={() => setTrackingOpen(true)}
                    className="inline-flex min-h-11 cursor-pointer items-center gap-1 text-xs font-medium text-accent hover:underline desktop:min-h-0"
                  >
                    <Clock className="size-3.5" /> Hành trình
                  </button>
                ) : null
              }
            >
              {carrierReported ? (
                <p className="mb-2.5 rounded-lg bg-paperSoft px-3 py-2 text-sm">
                  <span className="font-medium text-ink">{o.shipment?.statusText || formatBestStatus(o.shipment?.statusCode)}</span>
                  {carrierUpdatedAt && <span className="text-xs text-muted"> · cập nhật {new Date(carrierUpdatedAt).toLocaleString("vi-VN")}</span>}
                </p>
              ) : (
                !isClosedCancelled(o.status) && (
                  <p className="mb-2.5 text-xs text-muted">
                    {effectiveShipmentCode
                      ? "Chưa có cập nhật từ bưu cục."
                      : "Đang chờ hãng cấp mã vận đơn."}
                  </p>
                )
              )}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <Fact label="Mã vận đơn">
                  <span className="select-all font-mono font-semibold text-accent">{effectiveShipmentCode ?? "Chưa có"}</span>
                </Fact>
                <Fact label="Cân nặng">
                  <span className="font-mono">{o.shipment?.weightGram ? `${o.shipment.weightGram}g` : "500g"}</span>
                  {Boolean(o.shipment?.lengthCm && o.shipment?.widthCm && o.shipment?.heightCm) && (
                    <span className="ml-1.5 text-xs text-muted">
                      {o.shipment!.lengthCm}×{o.shipment!.widthCm}×{o.shipment!.heightCm} cm
                    </span>
                  )}
                </Fact>
                {Boolean(o.shipment?.declaredValue) && (
                  <Fact label="Khai giá">
                    <span className="font-mono">{money(o.shipment!.declaredValue!)}</span>
                  </Fact>
                )}
                <Fact label="Tên hàng" className="col-span-2 sm:col-span-3">
                  {o.shipment?.productName || "Hàng hóa tiêu chuẩn"}
                </Fact>
              </dl>
            </Section>
          )}
        </div>

        <div className="space-y-3">
          {/* Nhân viên không có quyền xem tiền: không có khối tiền (máy chủ đã không trả sổ tiền / giá vốn) */}
          {seesMoney && <Section title="Tiền của đơn">
            <OrderMoneyDetails
              money={o.money}
              seller={!readOnly}
              hasShipment={Boolean(o.shipment || effectiveShipmentCode || sched)}
              cod={o.cod}
              cost={readOnly ? undefined : o.cost}
            />
            {canClaimReturn && (
              <div className="mt-3 space-y-2.5 border-t border-rule pt-3">
                {claimSent ? (
                  <ReturnClaimSent deadline={claimDeadline} />
                ) : claimClosed ? (
                  <p className="text-sm text-inkSoft">
                    Đã quá hạn báo thiếu / hỏng hàng hoàn (hạn: <span className="tabular-nums">{formatDeadline(claimDeadline)}</span>).
                  </p>
                ) : (
                  <p className="text-sm text-inkSoft">
                    Hãng đã trả hàng về.
                    {claimDeadline && (
                      <span className="mt-1 block font-medium text-ink">
                        Hạn báo: <span className="tabular-nums">{formatDeadline(claimDeadline)}</span>
                      </span>
                    )}
                  </p>
                )}
                {!claimClosed && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setClaimOpen(true)}>
                    <PackageX className="size-4" /> {claimSent ? "Báo bổ sung" : "Báo thiếu / hỏng hàng hoàn"}
                  </Button>
                )}
              </div>
            )}
          </Section>}

          {/* Tiến trình xử lý (saga) — một dòng; lỗi thì hiện lý do + nút thử lại */}
          {sagaId && saga.data && (
            <section data-surface="solid" className="rounded-xl bg-raise p-3.5 shadow-card sm:px-4">
              <div className="flex items-center gap-2 text-sm">
                <SagaStateIcon state={saga.data.saga.state} />
                <span className="text-inkSoft">Tiến trình:</span>
                <span className="font-medium text-ink">{sagaStateLabel(saga.data.saga.state)}</span>
                <span className="ml-auto text-right text-xs text-muted">
                  {sagaTypeLabel(saga.data.saga.type)}
                  {saga.data.saga.attempts > 1 && ` · thử ${saga.data.saga.attempts} lần`}
                </span>
              </div>
              {saga.data.saga.lastError && (
                <div className="mt-2.5 space-y-2 rounded border border-danger/20 bg-danger/10 p-2.5 text-xs text-danger">
                  <p className="font-semibold leading-relaxed">{saga.data.saga.lastError.replace(/^(App)?Error:\s*/, "")}</p>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    {saga.data.saga.lastError.includes("địa chỉ kho") && (
                      <Link
                        to="/settings?tab=warehouse"
                        className="inline-block rounded bg-danger px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-danger/90"
                      >
                        Kho lấy hàng
                      </Link>
                    )}
                    {can({ any: ["orders.create", "orders.edit"] }) && <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 border-danger/30 bg-white px-2 text-xs text-danger hover:bg-danger/10"
                      onClick={() => retrySaga.mutate()}
                      disabled={retrySaga.isPending}
                    >
                      {retrySaga.isPending ? "Đang thử lại..." : "Thử lại"}
                    </Button>}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Lịch sử đơn chỉ đọc được từ DB của chủ đơn — đơn cấp dưới không có. */}
          {!readOnly && <OrderTimeline orderId={o.id} />}
        </div>
      </div>

      {/* Điện thoại: thao tác ở thanh dưới cùng — không phải cuộn hết trang mới thấy */}
      {(canEdit || printCode) && (
        <MobileActionBar>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={onCancel} className="text-danger hover:text-danger">
              Hủy đơn
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className={cn(!printCode && !canSendNow && "flex-1")}>
              <Pencil /> Sửa đơn
            </Button>
          )}
          {canSendNow && (
            <Button size="sm" disabled={sendNow.isPending} onClick={onSendNow} className="flex-1">
              <Send /> Gửi ngay
            </Button>
          )}
          {printCode && (
            <Button size="sm" disabled={printing} onClick={() => handleWaybill(printCode, "print")} className="flex-1">
              <Printer /> In vận đơn
            </Button>
          )}
        </MobileActionBar>
      )}

      {claimOpen && (
        <ReturnClaimDialog
          orderId={o.id}
          waybillCode={effectiveShipmentCode}
          deadline={claimDeadline}
          onClose={() => setClaimOpen(false)}
          onSent={(deadline) => {
            setClaimOpen(false)
            setClaimSent({ deadline })
            toast.success(`Đã gửi báo cáo hàng hoàn cho ${nppName}`)
          }}
        />
      )}

      {editOpen && !readOnly && (
        <EditOrderSheet
          order={o}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); order.refetch() }}
        />
      )}

      {trackingOpen && effectiveShipmentCode && (
        <TrackingModal
          code={effectiveShipmentCode}
          orderId={o.id}
          ownerShopId={ownerShopId}
          readOnly={readOnly}
          open={trackingOpen}
          onClose={() => setTrackingOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Tracking Modal ─────────────────────────────────
// Tab "Hành trình": có tên bưu cục (GET /orders/:id/tracking — bản tra được lưu lại, gộp dần, không mất bước cũ).
// Tab "Trạng thái BEST": Order/Query như trước — chỉ chủ đơn; tra luôn khi mở để ghi bước mới nhất vào đơn.

function TrackingModal({
  code,
  orderId,
  ownerShopId,
  readOnly,
  open,
  onClose,
}: {
  code: string
  orderId: string
  ownerShopId: string | null
  readOnly: boolean
  open: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<"journey" | "best" | null>(null)
  const livePath = `/orders/${encodeURIComponent(orderId)}/tracking${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`
  const liveKey = ["order-tracking", orderId, ownerShopId]
  const live = useQuery({
    queryKey: liveKey,
    queryFn: () => get<LiveTrackingResult>(livePath),
    enabled: open,
    staleTime: 60_000,
  })
  const refresh = useMutation({
    mutationFn: () => get<LiveTrackingResult>(`${livePath}${ownerShopId ? "&" : "?"}refresh=1`),
    onSuccess: (d) => qc.setQueryData(liveKey, d),
  })

  const query = useQuery({
    queryKey: ["best-query", code],
    queryFn: async () => {
      const res = await post<any>("/shipping/query", { codes: [code], langType: "en-US" })
      const raw = res?.result?.data?.[0] ?? res?.result?.[0] ?? res?.result
      return raw
    },
    enabled: open && !readOnly,
  })

  const traceLogs = query.data?.traceLogs ?? []
  const steps = journeySteps(live.data)
  // Chưa có hành trình bưu cục (chưa cấu hình / lỗi / BEST chưa có) → chủ đơn xem trạng thái BEST
  const current = tab ?? (live.isLoading || steps.length > 0 || readOnly ? "journey" : "best")

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* Cột flex: danh sách chiếm phần còn lại và tự cuộn — nút Đóng luôn trong khung (vh trên iPhone gồm cả
          phần dưới thanh Safari nên max-h theo vh từng đẩy nút xuống dưới mép) */}
      <DialogContent className="flex max-w-md flex-col">
        <DialogHeader>
          <DialogTitle>Hành trình</DialogTitle>
          <DialogDescription className="text-xs font-mono text-muted">
            Mã vận đơn: {code}
          </DialogDescription>
        </DialogHeader>

        {!readOnly && (
          <Segmented
            label="Kiểu xem"
            value={current}
            onChange={setTab}
            options={[["journey", "Hành trình"], ["best", "Trạng thái vận chuyển"]] as const}
            className="shrink-0 self-start"
          />
        )}

        {current === "journey" ? (
          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            <LiveJourney
              result={live.data}
              loading={live.isLoading}
              error={live.isError ? (live.error as Error) : null}
              refreshing={refresh.isPending}
              onRefresh={() => refresh.mutate()}
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto py-4">
            {query.isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted">
                 Đang tra cứu hành trình…
              </div>
            ) : query.isError ? (
              <div className="text-danger text-sm py-4 text-center">
                Không thể tra cứu: {(query.error as Error).message}
              </div>
            ) : traceLogs.length === 0 ? (
              <div className="text-inkSoft text-sm py-6 text-center">
                Chưa có hành trình
              </div>
            ) : (
              <ol className="space-y-3 relative before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-rule">
                {traceLogs.map((log: any, idx: number) => {
                  const dateStr = log.modified_at
                    ? new Date(typeof log.modified_at === "number" ? log.modified_at : log.modified_at).toLocaleString("vi-VN")
                    : log.time || log.created_at || "—"
                  const statusCode = log.status ?? log.Status ?? log.statusCode ?? log.status_code ?? log.code
                  const statusTitle = formatBestStatus(statusCode, log.status_text || log.statusText || log.note)
                  const content = log.status_content || log.statusContent || log.remark || log.reason
                  return (
                    <li key={idx} className="flex gap-4 items-start pl-1 relative">
                      <div className="size-4 rounded-full ring-2 ring-paper shrink-0 mt-0.5 bg-accent" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm font-medium text-ink">
                            {statusTitle}
                          </p>
                          <p className="text-xs text-muted tabular-nums whitespace-nowrap">{dateStr}</p>
                        </div>
                        {content && (
                          <p className="text-xs text-inkSoft mt-0.5">
                            Chi tiết: <span className="text-ink">{content}</span>
                          </p>
                        )}
                        {log.location && <p className="text-xs text-muted mt-0.5">Vị trí: {log.location}</p>}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted font-mono">
                          {log.cod !== undefined && log.cod > 0 && (
                            <span>COD: {money(log.cod)}</span>
                          )}
                          {log.weight !== undefined && log.weight > 0 && (
                            <span>TL: {log.weight}g</span>
                          )}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        )}

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose}>
            Đóng
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Edit sheet ─────────────────────────────────────

/** Dòng hàng trong form sửa đơn hẹn gửi */
interface EditLine {
  productId: string
  name: string
  price: number
  qty: number
  weightGram: number
}

/** Tên kiện như máy chủ dựng từ dòng hàng (apps/api/src/lib/orderPackage.ts) */
const packageNameOf = (lines: EditLine[]) => lines.map((l) => `${l.name}${l.qty > 1 ? ` (x${l.qty})` : ""}`).join(", ").slice(0, 200)
const lineKey = (lines: EditLine[]) => JSON.stringify(lines.map((l) => [l.productId, l.qty, l.price]).sort())

function EditOrderSheet({
  order, onClose, onSaved,
}: { order: Order; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient()
  // Đơn hẹn gửi: chưa lên hãng — sửa được cả sản phẩm, người nhận, giờ hẹn; COD tăng hay giảm đều được
  const sched = order.status === "hen-gio" && order.schedule?.shipment ? order.schedule : null
  const scheduled = Boolean(sched)
  const parcel = order.shipment ?? sched?.shipment ?? null
  const hasShipment = Boolean(order.shipment?.code)
  const editsParcel = hasShipment || scheduled
  const statusCode = order.shipment?.statusCode
  const addressLocked = hasShipment && Number(statusCode ?? 0) >= 303

  const [form, setForm] = useState({
    cod: order.cod,
    address: order.address,
    destCity: parcel?.destCity ?? "",
    destDistrict: parcel?.destDistrict ?? "",
    destWard: parcel?.destWard ?? "",
    productName: parcel?.productName ?? "",
    weightGram: parcel?.weightGram ?? 0,
  })

  const initialSendAt = sched ? toLocalInput(new Date(sched.sendAt)) : ""
  const [sendAtLocal, setSendAtLocal] = useState(initialSendAt)
  const sendAtProblem = scheduled && sendAtLocal !== initialSendAt ? sendAtError(fromLocalInput(sendAtLocal)) : null
  const [recipient, setRecipient] = useState({ name: order.customer.name, phone: order.customer.phone ?? "" })

  const initialLines: EditLine[] = (order.items ?? [])
    .filter((it) => it.productId)
    .map((it) => ({ productId: String(it.productId), name: it.productName, price: it.price, qty: it.qty, weightGram: it.weightGram }))
  // Đơn cũ có dòng không gắn sản phẩm trong kho → không sửa sản phẩm được (máy chủ chỉ nhận sản phẩm trong kho)
  const linesEditable = scheduled && initialLines.length > 0 && initialLines.length === (order.items ?? []).length
  const [lines, setLines] = useState<EditLine[]>(initialLines)
  const linesChanged = linesEditable && lineKey(lines) !== lineKey(initialLines)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [nameTouched, setNameTouched] = useState(false)
  const [weightTouched, setWeightTouched] = useState(false)

  const { data: productData } = useQuery({
    queryKey: ["products-active"],
    queryFn: () => get<{ products: ProductOpt[] }>("/products?active=true"),
    enabled: linesEditable,
    staleTime: 30_000,
  })
  // Kho đã trừ phần của đơn này lúc lên đơn — số có thể đặt = tồn hiện tại + phần đơn đang giữ
  const held = new Map(initialLines.map((l) => [l.productId, l.qty]))
  const pickerProducts = productData?.products?.map((p) => (held.has(p.id) ? { ...p, stock: p.stock + held.get(p.id)! } : p))
  const maxQtyOf = (productId: string) => pickerProducts?.find((p) => p.id === productId)?.stock ?? Infinity
  const inCart = Object.fromEntries(lines.map((l) => [l.productId, l.qty]))
  const linesQty = lines.reduce((s, l) => s + l.qty, 0)
  const linesTotal = lines.reduce((s, l) => s + l.qty * l.price, 0)

  // Đổi sản phẩm → tên kiện / cân chạy theo (cho tới khi người sửa tự sửa hai ô này)
  useEffect(() => {
    if (!linesChanged) return
    setForm((prev) => ({
      ...prev,
      ...(nameTouched ? {} : { productName: packageNameOf(lines) || prev.productName }),
      ...(weightTouched ? {} : { weightGram: Math.max(50, lines.reduce((s, l) => s + l.qty * l.weightGram, 0)) }),
    }))
  }, [lines]) // eslint-disable-line react-hooks/exhaustive-deps -- chỉ chạy khi dòng hàng đổi

  const setLineQty = (p: ProductOpt, qty: number) => {
    const next = Math.min(p.stock, Math.max(0, Math.floor(qty)))
    setLines((cur) => {
      const idx = cur.findIndex((l) => l.productId === p.id)
      if (idx < 0) return next > 0 ? [...cur, { productId: p.id, name: p.name, price: p.price || 0, qty: next, weightGram: p.weightGram || 0 }] : cur
      return next > 0 ? cur.map((l, i) => (i === idx ? { ...l, qty: next } : l)) : cur.filter((_, i) => i !== idx)
    })
  }
  const patchLine = (idx: number, change: Partial<EditLine>) => setLines((cur) => cur.map((l, i) => (i === idx ? { ...l, ...change } : l)))

  const [mapOpen, setMapOpen] = useState(false)
  /** Khu vực vừa sửa chưa đủ chắc để gửi BEST — chặn lưu khi có đổi khu vực */
  const [areaIssue, setAreaIssue] = useState<AreaIssue | null>(null)
  const [lat, setLat] = useState<number | null>(order.lat ?? null)
  const [lng, setLng] = useState<number | null>(order.lng ?? null)
  const [geoAddress, setGeoAddress] = useState<string | null>(order.geoAddress ?? null)

  const { data: provinceList = [] } = useQuery({
    queryKey: ["provinces-api"],
    queryFn: fetchProvincesApi,
    staleTime: 1000 * 60 * 60 * 24 * 7,
  })

  const handleMapConfirm = async (picked: MapPickedAddress) => {
    const resolved = resolveAdministrativeAddress({
      city: picked.city,
      district: picked.district,
      ward: picked.ward,
      street: picked.address,
      fullAddress: picked.formattedAddress,
    })

    // Biết phường CŨ của điểm ghim → điền địa chỉ cũ; không thì địa chỉ 2 cấp (đơn vị hành chính mới): không có
    // quận/huyện — vẫn điền phường/xã
    const { city, district, ward } = picked.old ?? await matchAdminDivisions(provinceList, {
      city: resolved.city || picked.city,
      district: resolved.district || picked.district,
      ward: resolved.ward || picked.ward,
      fullText: picked.formattedAddress,
    })
    const address = resolved.street || picked.address

    setForm((prev) => ({
      ...prev,
      address: address || prev.address,
      ...(city ? { destCity: city, destDistrict: district, destWard: ward } : ward ? { destWard: ward } : {}),
    }))

    setLat(picked.lat)
    setLng(picked.lng)
    setGeoAddress(picked.formattedAddress)
    const summary = [address, ward, district, city].filter(Boolean).join(", ")
    toast.success(`Đã định vị thành công: ${summary}`)
  }

  // Chặn ở giao diện: không giảm COD xuống dưới cước theo bảng giá của người bán (đơn có vận đơn).
  // Chỉ xét khi COD bị sửa — đơn cũ tạo trước quy tắc này vẫn sửa được các mục khác.
  const minCod = hasShipment ? order.money?.sellerPrice ?? null : null
  const codTooLow = minCod != null && form.cod !== order.cod && form.cod < minCod
  // Chỉ vận đơn đã tạo mới bị giới hạn chỉ giảm COD (luật của hãng)
  const codIncreaseBlocked = !scheduled && form.cod > order.cod

  const save = useMutation({
    mutationFn: () => {
      if (codTooLow) {
        return Promise.reject(new Error(`COD không được nhỏ hơn cước ship ${money(minCod!)}`))
      }
      if (editsParcel && !addressLocked && !form.destWard.trim()) {
        return Promise.reject(new Error("Vui lòng chọn Phường / Xã nhận hàng"))
      }
      const patch: any = {}
      if (form.cod !== order.cod) patch.cod = form.cod
      if (form.address !== order.address && !addressLocked) patch.address = form.address
      if (editsParcel && !addressLocked && form.destCity !== parcel?.destCity) patch.destCity = form.destCity
      // "" và null đều là "không có quận" — so như nhau để chỉ sửa COD không gửi kèm quận
      if (editsParcel && !addressLocked && (form.destDistrict || null) !== (parcel?.destDistrict || null)) patch.destDistrict = form.destDistrict || null
      if (editsParcel && !addressLocked && form.destWard !== parcel?.destWard) patch.destWard = form.destWard
      if (linesChanged) {
        if (!lines.length) return Promise.reject(new Error("Chọn ít nhất một sản phẩm từ kho"))
        patch.items = lines.map((l) => ({ productId: l.productId, qty: l.qty, price: l.price }))
        // Máy chủ dựng lại tên kiện / cân từ sản phẩm — chỉ gửi khi người sửa tự sửa
        if (nameTouched) patch.productName = form.productName
        if (weightTouched) patch.weightGram = form.weightGram
      } else {
        if (editsParcel && form.productName !== parcel?.productName) patch.productName = form.productName
        if (editsParcel && form.weightGram !== parcel?.weightGram) patch.weightGram = form.weightGram
      }
      if (lat !== order.lat) patch.lat = lat
      if (lng !== order.lng) patch.lng = lng
      if (geoAddress !== order.geoAddress) patch.geoAddress = geoAddress
      if (scheduled) {
        if (sendAtLocal !== initialSendAt) {
          const at = fromLocalInput(sendAtLocal)
          const problem = sendAtError(at)
          if (problem) return Promise.reject(new Error(problem))
          patch.sendAt = at!.toISOString()
        }
        const name = recipient.name.trim()
        if (name !== order.customer.name) {
          if (!name) return Promise.reject(new Error("Nhập tên người nhận"))
          patch.recipientName = name
        }
        const phone = (recipient.phone ?? "").replace(/[\s.-]/g, "")
        if (phone !== (order.customer.phone ?? "")) {
          if (!isValidVnPhone(phone)) return Promise.reject(new Error("Số điện thoại người nhận không đúng"))
          patch.recipientPhone = phone
        }
      }
      // Có đổi khu vực thì khu vực mới phải đủ chắc để BEST giao đúng; không đổi (chỉ sửa COD…) thì không chặn
      const areaEdited = patch.destCity !== undefined || patch.destDistrict !== undefined || patch.destWard !== undefined
      if (areaEdited && areaIssue) {
        return Promise.reject(new Error(areaIssue === "unrecognized"
          ? "Chưa nhận ra khu vực giao — chọn lại Khu vực"
          : "Chọn phường cũ đúng với nhà khách"))
      }
      if (Object.keys(patch).length === 0) return Promise.resolve({ noop: true })
      return api(`/orders/${order.id}`, { method: "PATCH", body: JSON.stringify(patch) })
    },
    onSuccess: () => {
      toast.success("Đã lưu thay đổi")
      // Đổi sản phẩm / số lượng → tồn kho đổi
      if (linesChanged) {
        for (const key of ["products", "products-active", "product"]) void qc.invalidateQueries({ queryKey: [key] })
      }
      void qc.invalidateQueries({ queryKey: ["orders"] })
      onSaved()
    },
    onError: (err) => {
      toast.error((err as Error).message)
    },
  })

  // Portal ra body: khung nằm trong vùng nội dung bị thanh trên cùng che mất tiêu đề / nút đóng (lớp xếp chồng riêng).
  // app-content giữ cỡ chữ như trong trang.
  return createPortal(
    <div className="app-content fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-paper h-[100dvh] p-6 shadow-xl overflow-y-auto space-y-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl">Sửa đơn {order.id}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="size-4" /></Button>
        </div>

        <div className="space-y-4 text-sm">
          {scheduled && (
            <div className="space-y-1">
              <Label htmlFor="edit-send-at">Hẹn gửi</Label>
              <Input
                id="edit-send-at"
                type="datetime-local"
                value={sendAtLocal}
                min={toLocalInput(new Date(Date.now() + SEND_AT_MIN_LEAD_MS))}
                onChange={(e) => setSendAtLocal(e.target.value)}
                aria-invalid={Boolean(sendAtProblem)}
                className={cn(sendAtProblem && "border-danger")}
              />
              {sendAtProblem && <p className="text-xs text-danger">{sendAtProblem}</p>}
            </div>
          )}

          {linesEditable && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Sản phẩm</Label>
                <span className="font-mono text-sm font-semibold text-accent">{money(linesTotal)}</span>
              </div>
              <ul className="divide-y divide-rule/60 rounded-lg border border-rule bg-raise">
                {lines.map((l, idx) => {
                  const max = maxQtyOf(l.productId)
                  return (
                    <li key={l.productId} className="space-y-1.5 px-3 py-2">
                      <div className="flex items-start gap-2">
                        <p className="min-w-0 flex-1 text-sm text-ink">{l.name}</p>
                        <button
                          type="button"
                          onClick={() => setLines((cur) => cur.filter((_, i) => i !== idx))}
                          aria-label={`Bỏ ${l.name} khỏi đơn`}
                          className="-my-2 -mr-2 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center text-muted hover:text-danger desktop:size-8"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex h-11 shrink-0 items-center rounded-md border border-rule bg-paper desktop:h-9">
                          <button
                            type="button"
                            onClick={() => (l.qty > 1 ? patchLine(idx, { qty: l.qty - 1 }) : setLines((cur) => cur.filter((_, i) => i !== idx)))}
                            aria-label={`Giảm số lượng ${l.name}`}
                            className="flex h-full w-11 cursor-pointer items-center justify-center text-inkSoft hover:text-ink desktop:w-9"
                          >
                            <Minus className="size-3.5" />
                          </button>
                          <span className="w-8 text-center font-mono text-sm font-semibold text-ink" aria-live="polite">{l.qty}</span>
                          <button
                            type="button"
                            onClick={() => patchLine(idx, { qty: l.qty + 1 })}
                            disabled={l.qty >= max}
                            aria-label={`Tăng số lượng ${l.name}`}
                            className="flex h-full w-11 cursor-pointer items-center justify-center text-inkSoft hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 desktop:w-9"
                          >
                            <Plus className="size-3.5" />
                          </button>
                        </div>
                        <MoneyInput
                          aria-label={`Đơn giá ${l.name}`}
                          value={l.price}
                          onValueChange={(v) => patchLine(idx, { price: Math.max(0, Math.round(Number(v) || 0)) })}
                          className="h-11 min-w-0 flex-1 desktop:h-9"
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
              <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} className="w-full">
                <Plus /> Thêm sản phẩm
              </Button>
            </div>
          )}

          <div className="space-y-1">
            <Label>{scheduled ? "Tiền thu hộ COD" : "Tiền thu hộ COD (chỉ được giảm)"}</Label>
            <MoneyInput value={form.cod} onValueChange={(v) => setForm({ ...form, cod: v })} />
            {linesChanged && form.cod === order.cod && (
              <p className="text-xs text-muted">Tiền hàng {money(linesTotal)}</p>
            )}
            {codIncreaseBlocked && (
              <p className="text-xs text-danger">Chỉ được giảm COD (tối đa {money(order.cod)})</p>
            )}
            {codTooLow && (
              <p className="text-xs text-danger">COD không được nhỏ hơn cước ship {money(minCod!)}.</p>
            )}
            {minCod != null && !codTooLow && form.cod <= order.cod && (
              <p className="text-xs text-muted">Tối thiểu {money(minCod)} (bằng cước ship).</p>
            )}
          </div>

          {scheduled && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="edit-recipient-name">Người nhận</Label>
                <Input
                  id="edit-recipient-name"
                  value={recipient.name}
                  onChange={(e) => setRecipient((r) => ({ ...r, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-recipient-phone">Điện thoại</Label>
                <PhoneInput
                  id="edit-recipient-phone"
                  hideIcon
                  value={recipient.phone}
                  onValueChange={(v) => setRecipient((r) => ({ ...r, phone: v ?? "" }))}
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Địa chỉ chi tiết</Label>
              {!addressLocked && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMapOpen(true)}
                  className="h-6 text-xs text-accent hover:text-accent/80 gap-1 px-1.5 cursor-pointer"
                >
                  <MapPin className="size-3" /> Bản đồ GPS
                </Button>
              )}
            </div>
            {editsParcel ? (
              <AddressInput
                idPrefix="edit-dest"
                onIssueChange={setAreaIssue}
                label={null}
                value={{ street: form.address, city: form.destCity, district: form.destDistrict || null, ward: form.destWard }}
                onChange={(v) =>
                  setForm((prev) => ({ ...prev, address: v.street, destCity: v.city, destDistrict: v.district ?? "", destWard: v.ward }))
                }
                disabled={addressLocked}
              />
            ) : (
              <Input
                value={form.address}
                disabled={addressLocked}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            )}
            {addressLocked && (
              <p className="text-xs text-muted">Đơn đã qua trung tâm khai thác — không thể sửa địa chỉ</p>
            )}
            {lat != null && lng != null && (
              <div className="flex items-center justify-between text-xs bg-accent/5 border border-accent/20 rounded p-1.5 text-accent">
                <div className="flex items-center gap-1 truncate">
                  <MapPin className="size-3 shrink-0" />
                  <span className="truncate">{geoAddress || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}</span>
                </div>
                {!addressLocked && (
                  <button
                    type="button"
                    onClick={() => {
                      setLat(null)
                      setLng(null)
                      setGeoAddress(null)
                    }}
                    className="text-muted hover:text-danger ml-1 shrink-0 cursor-pointer"
                  >
                    Xóa tọa độ
                  </button>
                )}
              </div>
            )}
          </div>

          {editsParcel && (
            <>
              <div className="space-y-1">
                <Label>Tên hàng hóa</Label>
                <Input
                  value={form.productName}
                  onChange={(e) => {
                    setNameTouched(true)
                    setForm({ ...form, productName: e.target.value })
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>Cân nặng (g)</Label>
                <Input
                  type="number"
                  value={form.weightGram}
                  onChange={(e) => {
                    setWeightTouched(true)
                    setForm({ ...form, weightGram: Number(e.target.value) })
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 pt-4 border-t border-rule pb-safe">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>Đóng</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || codIncreaseBlocked || codTooLow || Boolean(sendAtProblem)}>
            {save.isPending ? "Đang lưu…" : "Lưu"}
          </Button>
        </div>

        <AddressMapPickerModal
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          onConfirm={handleMapConfirm}
          initialLat={lat ?? undefined}
          initialLng={lng ?? undefined}
          initialAddressText={[form.address, form.destWard, form.destDistrict, form.destCity].filter(Boolean).join(", ")}
        />
        {linesEditable && (
          <ProductPickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            products={pickerProducts}
            inCart={inCart}
            cartQty={linesQty}
            cartTotal={linesTotal}
            onSetQty={setLineQty}
          />
        )}
      </div>
    </div>,
    document.body,
  )
}

// ─── UI bits ──────────────────────────────────────────

function Section({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section data-surface="solid" className="rounded-xl bg-raise p-3.5 shadow-card sm:p-4">
      <div className="mb-2 flex min-h-6 items-center gap-2">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {aside && <div className="ml-auto flex shrink-0 items-center">{aside}</div>}
      </div>
      {children}
    </section>
  )
}

function Fact({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  )
}

function SagaStateIcon({ state }: { state: string }) {
  if (state === "done") return <CheckCircle2 className="size-4 shrink-0 text-ok" />
  if (state === "compensated" || state === "failed") return <XCircle className="size-4 shrink-0 text-danger" />
  if (state === "running" || state === "compensating") return <RefreshCw className="size-4 shrink-0 animate-spin text-info" />
  return <Clock className="size-4 shrink-0 text-warn" />
}

/** Đơn đã hủy / đang hủy — không sửa, không hủy lại. (draft_failed vẫn xử lý tiếp được.) */
function isClosedCancelled(status?: string | null): boolean {
  return (isCancelledStatus(status) && status !== "draft_failed") || status === "cancelled" || status === "da-huy" || status === "cancelling"
}

/** Trang đơn mạng lưới theo vai trò — nơi Đại lý / CTV mở đơn cấp dưới. */
function networkOrdersPath(): string {
  const auth = getAuthState()
  const t = auth.shops.find((s) => s.shopId === auth.activeShopId)?.tenantType
  return t === "ctv" ? "/ctv/orders" : t === "agency" ? "/agency/orders" : "/orders"
}

function sagaStateLabel(state: string): string {
  return {
    pending: "Đang chờ",
    running: "Đang xử lý",
    done: "Xong",
    compensating: "Đang hủy lại các bước đã làm",
    compensated: "Không thành công — đã hủy",
    failed: "Không thành công",
  }[state] ?? state
}

function sagaTypeLabel(type: string): string {
  return {
    createOrderWithShipment: "Tạo đơn + vận đơn",
    cancelShipment: "Hủy đơn + vận đơn",
    updateShipment: "Cập nhật vận đơn",
  }[type] ?? type
}

interface TimelineEvent {
  at: string
  type: string
  label: string
  detail?: string
  intent?: "ok" | "warn" | "danger" | "info"
}

/** Số sự kiện hiện sẵn — còn lại mở bằng "Xem thêm" để trang không dài theo lịch sử đơn. */
const TIMELINE_PREVIEW = 4

function OrderTimeline({ orderId }: { orderId: string }) {
  const [expanded, setExpanded] = useState(false)
  const q = useQuery({
    queryKey: ["order-timeline", orderId],
    queryFn: () => get<{ events: TimelineEvent[] }>(`/orders/${orderId}/timeline`),
    refetchInterval: 30_000,
  })

  const events = q.data?.events ?? []
  if (!events.length) return null
  const shown = expanded ? events : events.slice(0, TIMELINE_PREVIEW)
  const hidden = events.length - shown.length

  return (
    <Section title="Lịch sử">
      <ol className="relative space-y-2 before:absolute before:bottom-2 before:left-[5px] before:top-2 before:w-px before:bg-rule">
        {shown.map((e, i) => (
          <li key={i} className="relative flex items-start gap-3">
            <div
              className={cn(
                "mt-1 size-3 shrink-0 rounded-full ring-2 ring-raise",
                e.intent === "ok" && "bg-ok",
                e.intent === "warn" && "bg-warn",
                e.intent === "danger" && "bg-danger",
                (e.intent === "info" || !e.intent) && "bg-inkSoft",
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm text-ink">{e.label}</p>
                <p className="whitespace-nowrap text-xs tabular-nums text-muted">{new Date(e.at).toLocaleString("vi-VN")}</p>
              </div>
              {e.detail && <p className="text-xs text-inkSoft">{e.detail}</p>}
            </div>
          </li>
        ))}
      </ol>
      {(hidden > 0 || expanded) && events.length > TIMELINE_PREVIEW && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex min-h-11 cursor-pointer items-center pl-6 text-xs font-medium text-accent hover:underline desktop:min-h-0"
        >
          {expanded ? "Thu gọn" : `Xem thêm ${hidden} sự kiện`}
        </button>
      )}
    </Section>
  )
}
